/**
 * Admin composer: create new or edit an in-house article that lives at
 * /articles/:slug alongside the imported ones.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Save, Trash2, Star } from "lucide-react";
import { resolveSiteSlug } from "@/config/siteConfig";

// IFT-native editorial sections. SocialPulse deploys keep using the
// legacy 'story' bucket via /sp-articles.
const IFT_SECTIONS = ["About", "Culture & Heritage", "Famous Families", "story"];

export default function ArticleCompose() {
  const { user } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.is_super_admin));
  const { slug: editSlug } = useParams();
  const nav = useNavigate();
  const isEdit = !!editSlug && editSlug !== "new";
  const isIft = resolveSiteSlug() === "indianfamilytree";

  const [f, setF] = useState({
    title: "", slug: "", excerpt: "", content_html: "",
    feature_image: "", tags: "", author: "", is_featured: false,
    section: isIft ? "Culture & Heritage" : "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    const loader = isIft
      ? http.get(`/articles/${editSlug}`).then((r) => {
          const a = r.data;
          setF({
            title: a.title || "",
            slug: a.slug || "",
            excerpt: a.subtitle || a.excerpt || "",
            content_html: a.content_html || a.body || "",
            feature_image: a.hero_image || a.feature_image || "",
            tags: (a.tags || []).join(", "),
            author: a.author_name || a.author || "",
            is_featured: !!a.is_featured,
            section: a.section || "Culture & Heritage",
          });
        })
      : http.get(`/sp-articles/${editSlug}`).then((r) => {
          const a = r.data;
          setF({
            title: a.title || "",
            slug: a.slug || "",
            excerpt: a.excerpt || "",
            content_html: a.content_html || "",
            feature_image: a.feature_image || "",
            tags: (a.tags || []).join(", "),
            author: a.author || "",
            is_featured: !!a.is_featured,
            section: "",
          });
        });
    loader.catch(() => toast.error("Failed to load article")).finally(() => setLoading(false));
  }, [isEdit, editSlug, isIft]);

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl">Admins only.</h1>
        <Link to="/articles" className="wofy-btn-primary mt-6 inline-flex">Back to articles</Link>
      </div>
    );
  }

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title is required");
    if (!f.content_html.trim()) return toast.error("Content is required");
    setBusy(true);
    try {
      if (isIft) {
        const tagsArr = f.tags.split(",").map((t) => t.trim()).filter(Boolean);
        if (isEdit) {
          // Send only content_html — never overwrite the markdown ``body``
          // (curated articles are seeded from /app/backend/content/articles/*.md
          // and losing markdown fidelity breaks the source of truth).
          await http.patch(`/articles/${editSlug}`, {
            title: f.title.trim(),
            subtitle: f.excerpt.trim() || null,
            content_html: f.content_html,
            hero_image: f.feature_image.trim() || null,
            author_name: f.author.trim() || null,
            tags: tagsArr,
            section: f.section || undefined,
          });
          toast.success("Article updated");
          nav(`/articles/${editSlug}`);
        } else {
          const { data } = await http.post("/articles/quick-create", {
            title: f.title.trim(),
            slug: f.slug.trim() || undefined,
            section: f.section || "Culture & Heritage",
            body: f.content_html,
            subtitle: f.excerpt.trim() || undefined,
            author_name: f.author.trim() || undefined,
            hero_image: f.feature_image.trim() || undefined,
            tags: tagsArr,
          });
          toast.success("Article published");
          nav(`/articles/${data.slug}`);
        }
      } else {
        const payload = {
          title: f.title.trim(),
          slug: f.slug.trim() || undefined,
          excerpt: f.excerpt.trim() || undefined,
          content_html: f.content_html,
          feature_image: f.feature_image.trim() || undefined,
          tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
          author: f.author.trim() || undefined,
          is_featured: f.is_featured,
        };
        if (isEdit) {
          await http.put(`/sp-articles/${editSlug}`, payload);
          toast.success("Article updated");
          nav(`/articles/${editSlug}`);
        } else {
          const { data } = await http.post("/sp-articles", payload);
          toast.success("Article published");
          nav(`/articles/${data.slug}`);
        }
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!isEdit || !window.confirm("Delete this article permanently?")) return;
    setBusy(true);
    try {
      await http.delete(isIft ? `/articles/${editSlug}` : `/sp-articles/${editSlug}`);
      toast.success("Deleted");
      nav("/articles");
    } catch (e) { toast.error("Failed to delete"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto p-16 text-center">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="article-compose">
      <Link to="/articles" className="inline-flex items-center gap-1 text-xs mb-4 opacity-70 hover:opacity-100">
        <ArrowLeft className="w-3.5 h-3.5" /> All articles
      </Link>
      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>{isEdit ? "EDIT" : "NEW"} ARTICLE</div>
      <h1 className="font-display text-4xl mb-6">{isEdit ? "Edit article" : "Publish a new article"}</h1>

      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Title *</span>
          <input data-testid="compose-title" required className="wofy-input mt-1" value={f.title} onChange={(e) => upd("title", e.target.value)} />
        </label>
        {isIft && (
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Section *</span>
            <select data-testid="compose-section" required className="wofy-input mt-1" value={f.section} onChange={(e) => upd("section", e.target.value)}>
              {IFT_SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Slug (auto if blank)</span>
            <input data-testid="compose-slug" className="wofy-input mt-1" value={f.slug} onChange={(e) => upd("slug", e.target.value)} placeholder="e.g. my-article-title" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Author (optional)</span>
            <input data-testid="compose-author" className="wofy-input mt-1" value={f.author} onChange={(e) => upd("author", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Feature image URL</span>
          <input data-testid="compose-feature-image" className="wofy-input mt-1" value={f.feature_image} onChange={(e) => upd("feature_image", e.target.value)} placeholder="https://…" />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Tags (comma-separated)</span>
          <input data-testid="compose-tags" className="wofy-input mt-1" value={f.tags} onChange={(e) => upd("tags", e.target.value)} placeholder="e.g. Travel, News, India" />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Excerpt (short summary)</span>
          <textarea data-testid="compose-excerpt" rows={2} className="wofy-input mt-1" value={f.excerpt} onChange={(e) => upd("excerpt", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Content (HTML supported) *</span>
          <textarea
            required
            data-testid="compose-content"
            rows={14}
            className="wofy-input mt-1 font-mono text-sm"
            value={f.content_html}
            onChange={(e) => upd("content_html", e.target.value)}
            placeholder="<p>Write your article… Basic HTML is supported (h2, p, strong, em, ul, ol, blockquote, img).</p>"
          />
        </label>
        {!isIft && (
          <label className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              checked={f.is_featured}
              onChange={(e) => upd("is_featured", e.target.checked)}
              className="w-4 h-4"
              data-testid="compose-featured"
            />
            <span className="text-sm inline-flex items-center gap-1">
              <Star className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} /> Feature as "Article of the Week" on the Home page
            </span>
          </label>
        )}

        <div className="flex justify-between items-center pt-4 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          {isEdit ? (
            <button type="button" onClick={del} disabled={busy} data-testid="compose-delete" className="text-xs inline-flex items-center gap-1 text-red-600 hover:underline">
              <Trash2 className="w-3.5 h-3.5" /> Delete article
            </button>
          ) : <span />}
          <button type="submit" disabled={busy} data-testid="compose-save" className="wofy-btn-primary">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : (isEdit ? "Save changes" : "Publish article")}
          </button>
        </div>
      </form>
    </div>
  );
}
